import {commitBookingDiary} from '../../services/bookingDiaryService';
import React, {
  useMemo,
  useState,
} from "react";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";
import { jsPDF } from "jspdf";
import { getBusinessDateKey } from "../../utils/businessDate";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const toAmount = (value) => {
  const number = Number(
    String(value ?? "")
      .replace(/₹/g, "")
      .replace(/,/g, "")
      .trim()
  );

  return Number.isFinite(number)
    ? number
    : 0;
};

const formatMoney = (value) =>
  `₹${toAmount(value).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;

const pad = (value) =>
  String(value).padStart(2, "0");

const toDateKey = (date) =>
  `${date.getFullYear()}-${pad(
    date.getMonth() + 1
  )}-${pad(date.getDate())}`;

const getTodayKey = () =>
  toDateKey(new Date());

const getTomorrowKey = () => {
  const date = new Date();
  date.setDate(
    date.getDate() + 1
  );
  return toDateKey(date);
};

const getDayAfterTomorrowKey =
  () => {
    const date = new Date();
    date.setDate(
      date.getDate() + 2
    );
    return toDateKey(date);
  };

const prettyDate = (dateKey) => {
  if (!dateKey) return "-";

  const date = new Date(
    `${dateKey}T00:00:00`
  );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return dateKey;
  }

  return date.toLocaleDateString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      weekday: "short",
    }
  );
};

const prettyTime = (time) => {
  if (!time) return "-";

  const [
    hours,
    minutes,
  ] = String(time)
    .split(":")
    .map(Number);

  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes)
  ) {
    return time;
  }

  const date = new Date();
  date.setHours(
    hours,
    minutes,
    0,
    0
  );

  return date.toLocaleTimeString(
    "en-IN",
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};

const createEmptyForm = () => {
  const today =
    getTodayKey();

  const tomorrow =
    getTomorrowKey();

  return {
    guestName: "",
    phoneNumber: "",

    checkInDate: today,
    checkInTime: "12:00",
    checkOutDate: tomorrow,
    checkOutTime: "11:00",

    roomCount: "1",
    roomPreference: "",
    occupancy: "Double",
    tariff: "",
    totalAmount: "",

    advanceAmount: "",
    paymentMode: "Cash",
    transactionReference: "",

    breakfastIncluded: false,
    gstIncluded: false,

    bookingSource: "Direct",
    status: "confirmed",
    note: "",
  };
};

const getBalance = (entry) =>
  Math.max(
    0,
    toAmount(
      entry.totalAmount
    ) -
      toAmount(
        entry.advanceAmount
      )
  );


const addDaysToDateKey = (
  dateKey,
  days = 1
) => {
  if (!dateKey) {
    return getTodayKey();
  }

  const date = new Date(
    `${dateKey}T00:00:00`
  );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return getTodayKey();
  }

  date.setDate(
    date.getDate() + days
  );

  return toDateKey(date);
};

const createEmptyBulkRoomGroup =
  () => ({
    roomType: "",
    roomCount: "1",
    occupancy: "Double",
    tariff: "",
  });

const createEmptyBulkDay = (
  date = getTodayKey()
) => ({
  date,
  groups: [
    createEmptyBulkRoomGroup(),
  ],
});

const createEmptyBulkForm =
  () => ({
    guestName: "",
    phoneNumber: "",
    bookingSource: "Direct",

    checkInTime: "12:00",
    checkOutTime: "23:00",

    extraPersonChargeEnabled: false,
    extraPersonRate: "",
    extraPersonCount: "0",

    days: [
      createEmptyBulkDay(
        getTodayKey()
      ),
    ],

    advanceAmount: "",
    paymentMode: "Cash",
    transactionReference: "",

    breakfastIncluded: false,
    gstIncluded: false,

    status: "confirmed",
    note: "",
  });

const normalizeBulkDays = (
  days = []
) =>
  (Array.isArray(days) ? days : [])
    .map((day) => ({
      date: clean(day?.date),
      groups: (
        Array.isArray(day?.groups)
          ? day.groups
          : []
      )
        .map((group) => ({
          roomType:
            clean(
              group?.roomType
            ),
          roomCount:
            Math.max(
              1,
              Number(
                group?.roomCount ||
                  1
              )
            ),
          occupancy:
            clean(
              group?.occupancy
            ) || "Double",
          tariff:
            Math.max(
              0,
              toAmount(
                group?.tariff
              )
            ),
        }))
        .filter(
          (group) =>
            group.roomType ||
            group.tariff > 0
        ),
    }))
    .filter(
      (day) =>
        day.date &&
        day.groups.length > 0
    )
    .sort(
      (a, b) =>
        a.date.localeCompare(
          b.date
        )
    );

const getBulkDayTotal = (
  day
) =>
  (day?.groups || []).reduce(
    (sum, group) =>
      sum +
      Math.max(
        1,
        Number(
          group?.roomCount || 1
        )
      ) *
        Math.max(
          0,
          toAmount(
            group?.tariff
          )
        ),
    0
  );

const getBulkBookingTotal = (
  days = []
) =>
  normalizeBulkDays(days).reduce(
    (sum, day) =>
      sum +
      getBulkDayTotal(day),
    0
  );

const getBulkExtraPersonTotal = (
  formOrEntry = {}
) => {
  const enabled =
    formOrEntry.extraPersonChargeEnabled ===
      true ||
    toAmount(
      formOrEntry.extraPersonRate
    ) > 0;

  if (!enabled) {
    return 0;
  }

  const rate = Math.max(
    0,
    toAmount(
      formOrEntry.extraPersonRate
    )
  );

  const count = Math.max(
    0,
    Number(
      formOrEntry.extraPersonCount ||
        0
    )
  );

  return rate * count;
};

const getBulkGrandTotal = (
  formOrEntry = {}
) =>
  getBulkBookingTotal(
    formOrEntry.days ||
      formOrEntry.dailyPlan ||
      []
  ) +
  getBulkExtraPersonTotal(
    formOrEntry
  );

const getBulkMaxRooms = (
  days = []
) =>
  normalizeBulkDays(days).reduce(
    (maximum, day) =>
      Math.max(
        maximum,
        (day.groups || []).reduce(
          (sum, group) =>
            sum +
            Math.max(
              1,
              Number(
                group.roomCount || 1
              )
            ),
          0
        )
      ),
    0
  );

const getBulkRoomSummary = (
  days = []
) => {
  const labels = [];

  normalizeBulkDays(days)
    .forEach((day) => {
      (day.groups || [])
        .forEach((group) => {
          const label =
            `${group.roomCount} ${group.roomType}`;

          if (
            label.trim() &&
            !labels.includes(label)
          ) {
            labels.push(label);
          }
        });
    });

  return labels.join(" + ");
};

const getBulkOccupancySummary = (
  days = []
) => {
  const values = [];

  normalizeBulkDays(days)
    .forEach((day) => {
      (day.groups || [])
        .forEach((group) => {
          if (
            group.occupancy &&
            !values.includes(
              group.occupancy
            )
          ) {
            values.push(
              group.occupancy
            );
          }
        });
    });

  if (values.length === 0) {
    return "Double";
  }

  return values.length === 1
    ? values[0]
    : "Mixed";
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");


const makeSafeFileName = (
  value,
  fallback = "Advance-Booking"
) => {
  const cleaned = clean(value)
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return cleaned || fallback;
};

const statusLabel = (
  status
) => {
  switch (status) {
    case "checked_in":
      return "Checked In";
    case "cancelled":
      return "Cancelled";
    case "no_show":
      return "No Show";
    default:
      return "Confirmed";
  }
};

const statusStyle = (
  status
) => {
  if (
    status === "checked_in"
  ) {
    return {
      background: "#dcfce7",
      color: "#166534",
    };
  }

  if (
    status === "cancelled"
  ) {
    return {
      background: "#fee2e2",
      color: "#991b1b",
    };
  }

  if (
    status === "no_show"
  ) {
    return {
      background: "#f3f4f6",
      color: "#4b5563",
    };
  }

  return {
    background: "#dbeafe",
    color: "#1d4ed8",
  };
};

/* -------------------------------------------------------------------------- */
/*                             Booking Diary Panel                            */
/* -------------------------------------------------------------------------- */

function BookingDiaryPanel({
  hotelId,
  hotelName,
}) {
  const [
    entries,
    setEntries,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    modalOpen,
    setModalOpen,
  ] = useState(false);

  const [
    viewEntry,
    setViewEntry,
  ] = useState(null);

  const [
    editingId,
    setEditingId,
  ] = useState("");

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    form,
    setForm,
  ] = useState(
    createEmptyForm
  );


  const [
    bulkModalOpen,
    setBulkModalOpen,
  ] = useState(false);

  const [
    bulkEditingId,
    setBulkEditingId,
  ] = useState("");

  const [
    bulkForm,
    setBulkForm,
  ] = useState(
    createEmptyBulkForm
  );

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("active");

  const [
    dateFilter,
    setDateFilter,
  ] = useState("");

  React.useEffect(() => {
    if (!hotelId) {
      setEntries([]);
      setLoading(false);
      return undefined;
    }

    const diaryQuery =
      query(
        collection(
          db,
          "bookingDiary"
        ),
        where(
          "hotelId",
          "==",
          hotelId
        )
      );

    return onSnapshot(
      diaryQuery,
      (snapshot) => {
        const list =
          snapshot.docs.map(
            (item) => ({
              id: item.id,
              ...item.data(),
            })
          );

        list.sort(
          (a, b) => {
            const aKey =
              `${a.checkInDate || ""} ${a.checkInTime || ""}`;

            const bKey =
              `${b.checkInDate || ""} ${b.checkInTime || ""}`;

            return aKey.localeCompare(
              bKey
            );
          }
        );

        setEntries(list);
        setLoading(false);
      },
      (error) => {
        console.error(
          "Booking diary load error:",
          error
        );

        setLoading(false);
      }
    );
  }, [hotelId]);

  const today =
    getTodayKey();

  const tomorrow =
    getTomorrowKey();

  const dayAfterTomorrow =
    getDayAfterTomorrowKey();

  const activeEntries =
    useMemo(
      () =>
        entries.filter(
          (entry) =>
            ![
              "cancelled",
              "no_show",
            ].includes(
              entry.status
            )
        ),
      [entries]
    );

  const todayEntries =
    useMemo(
      () =>
        activeEntries.filter(
          (entry) =>
            entry.checkInDate ===
            today
        ),
      [activeEntries, today]
    );

  const tomorrowEntries =
    useMemo(
      () =>
        activeEntries.filter(
          (entry) =>
            entry.checkInDate ===
            tomorrow
        ),
      [
        activeEntries,
        tomorrow,
      ]
    );

  const upcomingEntries =
    useMemo(
      () =>
        activeEntries.filter(
          (entry) =>
            clean(
              entry.checkInDate
            ) >=
            dayAfterTomorrow
        ),
      [
        activeEntries,
        dayAfterTomorrow,
      ]
    );

  const filteredAll =
    useMemo(() => {
      const term =
        clean(search).toLowerCase();

      return entries.filter(
        (entry) => {
          if (
            statusFilter ===
              "active" &&
            [
              "cancelled",
              "no_show",
            ].includes(
              entry.status
            )
          ) {
            return false;
          }

          if (
            statusFilter !==
              "all" &&
            statusFilter !==
              "active" &&
            entry.status !==
              statusFilter
          ) {
            return false;
          }

          if (
            dateFilter &&
            entry.checkInDate !==
              dateFilter
          ) {
            return false;
          }

          if (!term) {
            return true;
          }

          const haystack = [
            entry.guestName,
            entry.phoneNumber,
            entry.roomPreference,
            entry.occupancy,
            entry.bookingSource,
            entry.note,
            entry.transactionReference,
          ]
            .map((value) =>
              clean(value)
                .toLowerCase()
            )
            .join(" ");

          return haystack.includes(
            term
          );
        }
      );
    }, [
      entries,
      search,
      statusFilter,
      dateFilter,
    ]);

  const resetForm =
    () => {
      setForm(
        createEmptyForm()
      );
      setEditingId("");
    };

  const openAdd =
    (presetDate = "") => {
      const next =
        createEmptyForm();

      if (presetDate) {
        next.checkInDate =
          presetDate;

        const checkout =
          new Date(
            `${presetDate}T00:00:00`
          );

        checkout.setDate(
          checkout.getDate() + 1
        );

        next.checkOutDate =
          toDateKey(checkout);
      }

      setForm(next);
      setEditingId("");
      setModalOpen(true);
      setViewEntry(null);
    };

  const resetBulkForm =
    () => {
      setBulkForm(
        createEmptyBulkForm()
      );
      setBulkEditingId("");
    };

  const openBulkAdd =
    (presetDate = "") => {
      const next =
        createEmptyBulkForm();

      if (presetDate) {
        next.days = [
          createEmptyBulkDay(
            presetDate
          ),
        ];
      }

      setBulkForm(next);
      setBulkEditingId("");
      setBulkModalOpen(true);
      setModalOpen(false);
      setViewEntry(null);
    };

  const openBulkEdit =
    (entry) => {
      const fallbackDays =
        Array.isArray(
          entry?.dailyPlan
        ) &&
        entry.dailyPlan.length > 0
          ? entry.dailyPlan
          : [
              {
                date:
                  entry.checkInDate ||
                  getTodayKey(),
                groups: [
                  {
                    roomType:
                      entry.roomPreference ||
                      "Room",
                    roomCount:
                      String(
                        entry.roomCount ||
                          1
                      ),
                    occupancy:
                      entry.occupancy ||
                      "Double",
                    tariff:
                      String(
                        entry.tariff ||
                          0
                      ),
                  },
                ],
              },
            ];

      setBulkForm({
        ...createEmptyBulkForm(),
        guestName:
          entry.guestName || "",
        phoneNumber:
          entry.phoneNumber || "",
        bookingSource:
          entry.bookingSource ||
          "Direct",
        checkInTime:
          entry.checkInTime ||
          "12:00",
        checkOutTime:
          entry.checkOutTime ||
          "23:00",
        extraPersonChargeEnabled:
          entry.extraPersonChargeEnabled ===
            true ||
          toAmount(
            entry.extraPersonRate
          ) > 0,
        extraPersonRate:
          String(
            entry.extraPersonRate ??
              ""
          ),
        extraPersonCount:
          String(
            entry.extraPersonCount ??
              "0"
          ),
        days:
          fallbackDays.map(
            (day) => ({
              date:
                day.date || "",
              groups:
                (day.groups || [])
                  .map((group) => ({
                    roomType:
                      group.roomType ||
                      "",
                    roomCount:
                      String(
                        group.roomCount ||
                          1
                      ),
                    occupancy:
                      group.occupancy ||
                      "Double",
                    tariff:
                      String(
                        group.tariff ??
                          ""
                      ),
                  })),
            })
          ),
        advanceAmount:
          String(
            entry.advanceAmount ??
              ""
          ),
        paymentMode:
          entry.paymentMode ||
          "Cash",
        transactionReference:
          entry.transactionReference ||
          "",
        breakfastIncluded:
          entry.breakfastIncluded ===
          true,
        gstIncluded:
          entry.gstIncluded ===
          true,
        status:
          entry.status ||
          "confirmed",
        note:
          entry.note || "",
      });

      setBulkEditingId(
        entry.id
      );
      setBulkModalOpen(true);
      setModalOpen(false);
      setViewEntry(null);
    };

  const updateBulkField = (
    field,
    value
  ) => {
    setBulkForm(
      (current) => ({
        ...current,
        [field]: value,
      })
    );
  };

  const updateBulkDayDate = (
    dayIndex,
    value
  ) => {
    setBulkForm(
      (current) => {
        const days = [
          ...current.days,
        ];

        days[dayIndex] = {
          ...days[dayIndex],
          date: value,
        };

        return {
          ...current,
          days,
        };
      }
    );
  };

  const updateBulkGroup = (
    dayIndex,
    groupIndex,
    field,
    value
  ) => {
    setBulkForm(
      (current) => {
        const days =
          current.days.map(
            (day, index) => {
              if (
                index !== dayIndex
              ) {
                return day;
              }

              const groups =
                day.groups.map(
                  (
                    group,
                    currentGroupIndex
                  ) =>
                    currentGroupIndex ===
                    groupIndex
                      ? {
                          ...group,
                          [field]:
                            value,
                        }
                      : group
                );

              return {
                ...day,
                groups,
              };
            }
          );

        return {
          ...current,
          days,
        };
      }
    );
  };

  const addBulkGroup = (
    dayIndex
  ) => {
    setBulkForm(
      (current) => ({
        ...current,
        days:
          current.days.map(
            (day, index) =>
              index === dayIndex
                ? {
                    ...day,
                    groups: [
                      ...day.groups,
                      createEmptyBulkRoomGroup(),
                    ],
                  }
                : day
          ),
      })
    );
  };

  const removeBulkGroup = (
    dayIndex,
    groupIndex
  ) => {
    setBulkForm(
      (current) => ({
        ...current,
        days:
          current.days.map(
            (day, index) => {
              if (
                index !== dayIndex
              ) {
                return day;
              }

              const groups =
                day.groups.filter(
                  (
                    _,
                    currentGroupIndex
                  ) =>
                    currentGroupIndex !==
                    groupIndex
                );

              return {
                ...day,
                groups:
                  groups.length > 0
                    ? groups
                    : [
                        createEmptyBulkRoomGroup(),
                      ],
              };
            }
          ),
      })
    );
  };

  const addBulkDay =
    () => {
      setBulkForm(
        (current) => {
          const lastDate =
            current.days[
              current.days.length -
                1
            ]?.date ||
            getTodayKey();

          return {
            ...current,
            days: [
              ...current.days,
              createEmptyBulkDay(
                addDaysToDateKey(
                  lastDate,
                  1
                )
              ),
            ],
          };
        }
      );
    };

  const duplicatePreviousBulkDay =
    () => {
      setBulkForm(
        (current) => {
          const previous =
            current.days[
              current.days.length -
                1
            ];

          if (!previous) {
            return current;
          }

          return {
            ...current,
            days: [
              ...current.days,
              {
                date:
                  addDaysToDateKey(
                    previous.date ||
                      getTodayKey(),
                    1
                  ),
                groups:
                  previous.groups.map(
                    (group) => ({
                      ...group,
                    })
                  ),
              },
            ],
          };
        }
      );
    };

  const removeBulkDay = (
    dayIndex
  ) => {
    setBulkForm(
      (current) => {
        if (
          current.days.length <= 1
        ) {
          return current;
        }

        return {
          ...current,
          days:
            current.days.filter(
              (_, index) =>
                index !== dayIndex
            ),
        };
      }
    );
  };

  const validateBulk =
    () => {
      if (
        !clean(
          bulkForm.guestName
        )
      ) {
        alert(
          "Guest name required hai."
        );
        return false;
      }

      const normalizedDays =
        normalizeBulkDays(
          bulkForm.days
        );

      if (
        normalizedDays.length ===
        0
      ) {
        alert(
          "Kam se kam ek booking day aur room category add karo."
        );
        return false;
      }

      for (
        let dayIndex = 0;
        dayIndex <
        bulkForm.days.length;
        dayIndex += 1
      ) {
        const day =
          bulkForm.days[
            dayIndex
          ];

        if (!day.date) {
          alert(
            `Day ${dayIndex + 1} ki date required hai.`
          );
          return false;
        }

        for (
          let groupIndex = 0;
          groupIndex <
          day.groups.length;
          groupIndex += 1
        ) {
          const group =
            day.groups[
              groupIndex
            ];

          if (
            !clean(
              group.roomType
            )
          ) {
            alert(
              `Day ${dayIndex + 1}, Room Group ${groupIndex + 1}: Room Type required hai.`
            );
            return false;
          }

          if (
            Number(
              group.roomCount
            ) <= 0
          ) {
            alert(
              `Day ${dayIndex + 1}, ${group.roomType}: valid room count enter karo.`
            );
            return false;
          }

          if (
            toAmount(
              group.tariff
            ) <= 0
          ) {
            alert(
              `Day ${dayIndex + 1}, ${group.roomType}: tariff enter karo.`
            );
            return false;
          }
        }
      }

      if (
        bulkForm.paymentMode !==
          "Cash" &&
        toAmount(
          bulkForm.advanceAmount
        ) > 0 &&
        !clean(
          bulkForm.transactionReference
        )
      ) {
        alert(
          "Online advance ka transaction code enter karo."
        );
        return false;
      }

      return true;
    };

  const saveBulkEntry =
    async () => {
      if (
        saving ||
        !validateBulk()
      ) {
        return;
      }

      setSaving(true);

      try {
        const dailyPlan =
          normalizeBulkDays(
            bulkForm.days
          );

        const roomPlanTotal =
          getBulkBookingTotal(
            dailyPlan
          );

        const extraPersonAmount =
          getBulkExtraPersonTotal(
            bulkForm
          );

        const totalAmount =
          roomPlanTotal +
          extraPersonAmount;

        const advanceAmount =
          Math.max(
            0,
            toAmount(
              bulkForm.advanceAmount
            )
          );

        const firstDate =
          dailyPlan[0].date;

        const lastDate =
          dailyPlan[
            dailyPlan.length - 1
          ].date;

        const payload = {
          hotelId,
          hotelName:
            hotelName || "",

          bookingType:
            "bulk_advance",

          guestName:
            clean(
              bulkForm.guestName
            ),

          phoneNumber:
            clean(
              bulkForm.phoneNumber
            ),

          checkInDate:
            firstDate,

          checkInTime:
            bulkForm.checkInTime ||
            "12:00",

          checkOutDate:
            addDaysToDateKey(
              lastDate,
              1
            ),

          checkOutTime:
            bulkForm.checkOutTime ||
            "23:00",

          extraPersonChargeEnabled:
            bulkForm.extraPersonChargeEnabled ===
              true,

          extraPersonRate:
            Math.max(
              0,
              toAmount(
                bulkForm.extraPersonRate
              )
            ),

          extraPersonCount:
            Math.max(
              0,
              Number(
                bulkForm.extraPersonCount ||
                  0
              )
            ),

          extraPersonAmount,

          dailyPlan,

          roomCount:
            getBulkMaxRooms(
              dailyPlan
            ),

          roomPreference:
            getBulkRoomSummary(
              dailyPlan
            ),

          occupancy:
            getBulkOccupancySummary(
              dailyPlan
            ),

          tariff: 0,

          totalAmount,

          advanceAmount,

          balanceAmount:
            Math.max(
              0,
              totalAmount -
                advanceAmount
            ),

          paymentMode:
            clean(
              bulkForm.paymentMode
            ) || "Cash",

          transactionReference:
            clean(
              bulkForm.transactionReference
            ),

          breakfastIncluded:
            bulkForm.breakfastIncluded ===
            true,

          gstIncluded:
            bulkForm.gstIncluded ===
            true,

          bookingSource:
            clean(
              bulkForm.bookingSource
            ) || "Direct",

          status:
            bulkForm.status ||
            "confirmed",

          note:
            clean(
              bulkForm.note
            ),

          updatedAt:
            serverTimestamp(),
        };

        const bookingRef = bulkEditingId ? doc(db, 'bookingDiary', bulkEditingId) : doc(collection(db, 'bookingDiary'));
        await commitBookingDiary({database: db, reference: bookingRef, hotelId,
          payload, previous: bulkEditingId ? entries.find(entry => entry.id === bulkEditingId) : null,
          create: !bulkEditingId});

        setBulkModalOpen(false);
        resetBulkForm();
      } catch (error) {
        console.error(
          "Bulk advance booking save error:",
          error
        );

        alert(
          "Bulk booking save nahi hui: " +
            (error?.message ||
              error)
        );
      } finally {
        setSaving(false);
      }
    };

  const downloadBulkBookingPdf =
    (entry) => {
      try {
        const dailyPlan =
          normalizeBulkDays(
            entry?.dailyPlan
          );

        const totalAmount =
          toAmount(
            entry?.totalAmount
          ) ||
          getBulkGrandTotal({
            ...entry,
            dailyPlan,
          });

        const advanceAmount =
          toAmount(
            entry?.advanceAmount
          );

        const balanceAmount =
          Math.max(
            0,
            totalAmount -
              advanceAmount
          );

        const pdf =
          new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
          });

        const pageWidth =
          pdf.internal.pageSize.getWidth();

        const pageHeight =
          pdf.internal.pageSize.getHeight();

        const margin = 14;
        const contentWidth =
          pageWidth - margin * 2;

        let y = 16;

        const moneyText = (
          value
        ) =>
          `INR ${toAmount(
            value
          ).toLocaleString(
            "en-IN",
            {
              minimumFractionDigits:
                2,
              maximumFractionDigits:
                2,
            }
          )}`;

        const ensureSpace = (
          requiredHeight = 16
        ) => {
          if (
            y + requiredHeight >
            pageHeight - 16
          ) {
            pdf.addPage();
            y = 16;
            return true;
          }

          return false;
        };

        const drawHeader =
          () => {
            pdf.setFillColor(
              15,
              23,
              42
            );
            pdf.roundedRect(
              margin,
              y,
              contentWidth,
              22,
              3,
              3,
              "F"
            );

            pdf.setTextColor(
              255,
              255,
              255
            );
            pdf.setFont(
              "helvetica",
              "bold"
            );
            pdf.setFontSize(16);
            pdf.text(
              "Advance Booking Confirmation",
              margin + 6,
              y + 9
            );

            pdf.setFontSize(9);
            pdf.setFont(
              "helvetica",
              "normal"
            );
            pdf.text(
              clean(
                entry?.hotelName ||
                  hotelName ||
                  "Hotel"
              ),
              margin + 6,
              y + 15
            );

            pdf.setFillColor(
              237,
              233,
              254
            );
            pdf.roundedRect(
              pageWidth - margin - 43,
              y + 5,
              37,
              10,
              3,
              3,
              "F"
            );

            pdf.setTextColor(
              109,
              40,
              217
            );
            pdf.setFont(
              "helvetica",
              "bold"
            );
            pdf.setFontSize(8);
            pdf.text(
              "BULK / GROUP",
              pageWidth -
                margin -
                24.5,
              y + 11.5,
              {
                align: "center",
              }
            );

            y += 29;
          };

        const drawInfoBox = (
          x,
          boxY,
          width,
          label,
          value
        ) => {
          pdf.setDrawColor(
            226,
            232,
            240
          );
          pdf.setFillColor(
            248,
            250,
            252
          );
          pdf.roundedRect(
            x,
            boxY,
            width,
            18,
            2,
            2,
            "FD"
          );

          pdf.setTextColor(
            100,
            116,
            139
          );
          pdf.setFont(
            "helvetica",
            "bold"
          );
          pdf.setFontSize(7);
          pdf.text(
            label.toUpperCase(),
            x + 4,
            boxY + 6
          );

          pdf.setTextColor(
            15,
            23,
            42
          );
          pdf.setFontSize(9);
          const lines =
            pdf.splitTextToSize(
              clean(value) || "-",
              width - 8
            );
          pdf.text(
            lines.slice(0, 2),
            x + 4,
            boxY + 12
          );
        };

        drawHeader();

        const gap = 4;
        const infoWidth =
          (contentWidth -
            gap * 2) /
          3;

        const infoRows = [
          [
            [
              "Guest",
              entry?.guestName ||
                "-",
            ],
            [
              "Phone",
              entry?.phoneNumber ||
                "-",
            ],
            [
              "Booking Source",
              entry?.bookingSource ||
                "-",
            ],
          ],
          [
            [
              "From",
              prettyDate(
                entry?.checkInDate
              ),
            ],
            [
              "To",
              prettyDate(
                entry?.checkOutDate
              ),
            ],
            [
              "Payment",
              `${entry?.paymentMode || "-"}${
                entry?.transactionReference
                  ? ` / ${entry.transactionReference}`
                  : ""
              }`,
            ],
          ],
        ];

        infoRows.forEach(
          (row) => {
            row.forEach(
              (
                [label, value],
                index
              ) => {
                drawInfoBox(
                  margin +
                    index *
                      (infoWidth +
                        gap),
                  y,
                  infoWidth,
                  label,
                  value
                );
              }
            );

            y += 22;
          }
        );

        y += 2;

        dailyPlan.forEach(
          (day) => {
            const required =
              18 +
              day.groups.length *
                11;

            ensureSpace(
              Math.min(
                required,
                70
              )
            );

            pdf.setFillColor(
              238,
              242,
              255
            );
            pdf.setDrawColor(
              199,
              210,
              254
            );
            pdf.roundedRect(
              margin,
              y,
              contentWidth,
              11,
              2,
              2,
              "FD"
            );

            pdf.setTextColor(
              49,
              46,
              129
            );
            pdf.setFont(
              "helvetica",
              "bold"
            );
            pdf.setFontSize(9);
            pdf.text(
              prettyDate(
                day.date
              ),
              margin + 4,
              y + 7
            );

            pdf.text(
              moneyText(
                getBulkDayTotal(
                  day
                )
              ),
              pageWidth -
                margin -
                4,
              y + 7,
              {
                align: "right",
              }
            );

            y += 14;

            const cols = {
              room: margin,
              occ: margin + 60,
              count: margin + 106,
              tariff: margin + 130,
              total:
                pageWidth -
                margin,
            };

            pdf.setTextColor(
              100,
              116,
              139
            );
            pdf.setFontSize(7);
            pdf.setFont(
              "helvetica",
              "bold"
            );

            pdf.text(
              "ROOM TYPE",
              cols.room,
              y
            );
            pdf.text(
              "OCCUPANCY",
              cols.occ,
              y
            );
            pdf.text(
              "ROOMS",
              cols.count,
              y,
              {
                align: "right",
              }
            );
            pdf.text(
              "TARIFF",
              cols.tariff,
              y,
              {
                align: "right",
              }
            );
            pdf.text(
              "DAY TOTAL",
              cols.total,
              y,
              {
                align: "right",
              }
            );

            y += 4;

            day.groups.forEach(
              (group) => {
                ensureSpace(11);

                const lineTotal =
                  Number(
                    group.roomCount
                  ) *
                  toAmount(
                    group.tariff
                  );

                pdf.setDrawColor(
                  226,
                  232,
                  240
                );
                pdf.line(
                  margin,
                  y + 7,
                  pageWidth -
                    margin,
                  y + 7
                );

                pdf.setTextColor(
                  15,
                  23,
                  42
                );
                pdf.setFont(
                  "helvetica",
                  "normal"
                );
                pdf.setFontSize(8);

                const roomType =
                  pdf.splitTextToSize(
                    clean(
                      group.roomType
                    ) || "-",
                    52
                  )[0];

                pdf.text(
                  roomType,
                  cols.room,
                  y + 4
                );

                pdf.text(
                  clean(
                    group.occupancy
                  ) || "-",
                  cols.occ,
                  y + 4
                );

                pdf.text(
                  String(
                    group.roomCount
                  ),
                  cols.count,
                  y + 4,
                  {
                    align: "right",
                  }
                );

                pdf.text(
                  moneyText(
                    group.tariff
                  ),
                  cols.tariff,
                  y + 4,
                  {
                    align: "right",
                  }
                );

                pdf.setFont(
                  "helvetica",
                  "bold"
                );
                pdf.text(
                  moneyText(
                    lineTotal
                  ),
                  cols.total,
                  y + 4,
                  {
                    align: "right",
                  }
                );

                y += 10;
              }
            );

            y += 5;
          }
        );

        ensureSpace(36);

        const moneyGap = 4;
        const moneyWidth =
          (contentWidth -
            moneyGap * 2) /
          3;

        const moneyCards = [
          {
            label:
              "TOTAL BOOKING",
            value:
              totalAmount,
            fill: [
              239,
              246,
              255,
            ],
            color: [
              29,
              78,
              216,
            ],
          },
          {
            label:
              "ADVANCE RECEIVED",
            value:
              advanceAmount,
            fill: [
              236,
              253,
              245,
            ],
            color: [
              21,
              128,
              61,
            ],
          },
          {
            label: "BALANCE",
            value:
              balanceAmount,
            fill: [
              255,
              241,
              242,
            ],
            color: [
              190,
              18,
              60,
            ],
          },
        ];

        moneyCards.forEach(
          (
            card,
            index
          ) => {
            const x =
              margin +
              index *
                (moneyWidth +
                  moneyGap);

            pdf.setDrawColor(
              219,
              227,
              239
            );
            pdf.setFillColor(
              ...card.fill
            );
            pdf.roundedRect(
              x,
              y,
              moneyWidth,
              24,
              2,
              2,
              "FD"
            );

            pdf.setTextColor(
              100,
              116,
              139
            );
            pdf.setFont(
              "helvetica",
              "bold"
            );
            pdf.setFontSize(7);
            pdf.text(
              card.label,
              x + 4,
              y + 7
            );

            pdf.setTextColor(
              ...card.color
            );
            pdf.setFontSize(12);
            pdf.text(
              moneyText(
                card.value
              ),
              x + 4,
              y + 17
            );
          }
        );

        y += 31;

        ensureSpace(35);

        pdf.setDrawColor(
          253,
          230,
          138
        );
        pdf.setFillColor(
          255,
          251,
          235
        );
        pdf.roundedRect(
          margin,
          y,
          contentWidth,
          37,
          2,
          2,
          "FD"
        );

        pdf.setTextColor(
          15,
          23,
          42
        );
        pdf.setFont(
          "helvetica",
          "bold"
        );
        pdf.setFontSize(8);
        pdf.text(
          "INCLUSIONS / NOTES",
          margin + 4,
          y + 6
        );

        pdf.setFont(
          "helvetica",
          "normal"
        );
        pdf.setFontSize(7.5);

        const noteText = [
          "In any circumstances refund is not possible.",
          "Rest amount must be paid at check-in.",
          "Check-in time: 12:00 PM | Check-out time: 11:00 AM.",
          entry?.extraPersonChargeEnabled &&
          toAmount(entry?.extraPersonRate) > 0
            ? `Extra person charge: ${moneyText(entry.extraPersonRate)} per person${Number(entry?.extraPersonCount || 0) > 0 ? ` x ${Number(entry.extraPersonCount)} = ${moneyText(entry.extraPersonAmount || getBulkExtraPersonTotal(entry))}` : ""}.`
            : "",
          entry?.breakfastIncluded
            ? "Breakfast Included"
            : "Breakfast Not Included",
          entry?.gstIncluded
            ? "GST Included"
            : "GST Extra / As Applicable",
          clean(entry?.note)
            ? `Note: ${clean(
                entry.note
              )}`
            : "",
        ]
          .filter(Boolean)
          .join(" | ");

        const noteLines =
          pdf.splitTextToSize(
            noteText,
            contentWidth - 8
          );

        pdf.text(
          noteLines.slice(0, 8),
          margin + 4,
          y + 12
        );

        y += 43;

        ensureSpace(24);

        pdf.setDrawColor(
          100,
          116,
          139
        );
        pdf.line(
          pageWidth -
            margin -
            55,
          y + 12,
          pageWidth - margin,
          y + 12
        );

        pdf.setTextColor(
          100,
          116,
          139
        );
        pdf.setFontSize(7);
        pdf.text(
          "Generated from Hotel PMS Booking Diary",
          margin,
          y + 13
        );
        pdf.text(
          "Authorized Signature",
          pageWidth -
            margin -
            27.5,
          y + 17,
          {
            align: "center",
          }
        );

        const guestPart =
          makeSafeFileName(
            entry?.guestName,
            "Guest"
          );

        const datePart =
          clean(
            entry?.checkInDate
          ) || getTodayKey();

        pdf.save(
          `Advance-Booking-${guestPart}-${datePart}.pdf`
        );
      } catch (error) {
        console.error(
          "Bulk booking PDF download error:",
          error
        );

        alert(
          "PDF download nahi hua: " +
            (error?.message ||
              error)
        );
      }
    };

  const printBulkBooking =
    (entry) => {
      const dailyPlan =
        normalizeBulkDays(
          entry?.dailyPlan
        );

      const totalAmount =
        toAmount(
          entry?.totalAmount
        ) ||
        getBulkBookingTotal(
          dailyPlan
        );

      const advanceAmount =
        toAmount(
          entry?.advanceAmount
        );

      const balanceAmount =
        Math.max(
          0,
          totalAmount -
            advanceAmount
        );

      const printWindow =
        window.open(
          "",
          "_blank",
          "width=980,height=900"
        );

      if (!printWindow) {
        alert(
          "Browser popup block kar raha hai. Print / Save PDF ke liye popups allow karo."
        );
        return;
      }

      const dayRows =
        dailyPlan
          .map(
            (day) => {
              const groupRows =
                day.groups
                  .map(
                    (group) => {
                      const lineTotal =
                        Number(
                          group.roomCount
                        ) *
                        toAmount(
                          group.tariff
                        );

                      return `
                        <tr>
                          <td>${escapeHtml(group.roomType)}</td>
                          <td>${escapeHtml(group.occupancy)}</td>
                          <td class="num">${Number(group.roomCount)}</td>
                          <td class="num">${formatMoney(group.tariff)}</td>
                          <td class="num strong">${formatMoney(lineTotal)}</td>
                        </tr>
                      `;
                    }
                  )
                  .join("");

              return `
                <section class="day-card">
                  <div class="day-title">
                    <span>${escapeHtml(prettyDate(day.date))}</span>
                    <strong>${formatMoney(getBulkDayTotal(day))}</strong>
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>Room Type</th>
                        <th>Occupancy</th>
                        <th class="num">Rooms</th>
                        <th class="num">Tariff / Room</th>
                        <th class="num">Day Total</th>
                      </tr>
                    </thead>
                    <tbody>${groupRows}</tbody>
                  </table>
                </section>
              `;
            }
          )
          .join("");

      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Advance Booking - ${escapeHtml(entry?.guestName || "Guest")}</title>
            <style>
              * { box-sizing: border-box; }
              body {
                margin: 0;
                padding: 28px;
                font-family: Arial, sans-serif;
                color: #0f172a;
                background: #fff;
              }
              .sheet {
                max-width: 900px;
                margin: 0 auto;
              }
              .top {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 20px;
                padding-bottom: 16px;
                border-bottom: 2px solid #0f172a;
              }
              h1 {
                margin: 0;
                font-size: 24px;
              }
              .hotel {
                margin-top: 5px;
                font-size: 13px;
                color: #475569;
                font-weight: 700;
              }
              .badge {
                display: inline-block;
                padding: 6px 10px;
                border-radius: 999px;
                background: #ede9fe;
                color: #6d28d9;
                font-size: 11px;
                font-weight: 800;
              }
              .guest-grid {
                display: grid;
                grid-template-columns: repeat(3,1fr);
                gap: 10px;
                margin: 18px 0;
              }
              .info {
                padding: 10px;
                border: 1px solid #e2e8f0;
                border-radius: 8px;
              }
              .label {
                color: #64748b;
                font-size: 9px;
                text-transform: uppercase;
                font-weight: 800;
              }
              .value {
                margin-top: 4px;
                font-size: 12px;
                font-weight: 800;
              }
              .day-card {
                margin-top: 14px;
                border: 1px solid #cbd5e1;
                border-radius: 10px;
                overflow: hidden;
              }
              .day-title {
                display: flex;
                justify-content: space-between;
                padding: 10px 12px;
                background: #f8fafc;
                border-bottom: 1px solid #cbd5e1;
                font-size: 13px;
                font-weight: 800;
              }
              table {
                width: 100%;
                border-collapse: collapse;
              }
              th, td {
                padding: 9px 10px;
                border-bottom: 1px solid #e2e8f0;
                text-align: left;
                font-size: 11px;
              }
              th {
                background: #f8fafc;
                color: #475569;
                font-size: 9px;
                text-transform: uppercase;
              }
              .num { text-align: right; }
              .strong { font-weight: 800; }
              .money {
                display: grid;
                grid-template-columns: repeat(3,1fr);
                gap: 10px;
                margin-top: 18px;
              }
              .money-box {
                padding: 14px;
                border-radius: 10px;
                border: 1px solid #dbe3ef;
              }
              .money-box.total { background: #eff6ff; }
              .money-box.advance { background: #ecfdf5; }
              .money-box.balance { background: #fff1f2; }
              .money-value {
                margin-top: 5px;
                font-size: 20px;
                font-weight: 900;
              }
              .note {
                margin-top: 14px;
                padding: 12px;
                background: #fffbeb;
                border: 1px solid #fde68a;
                border-radius: 8px;
                font-size: 11px;
                line-height: 1.5;
              }
              .footer {
                margin-top: 28px;
                display: flex;
                justify-content: space-between;
                gap: 40px;
                font-size: 11px;
                color: #64748b;
              }
              .signature {
                width: 220px;
                padding-top: 28px;
                border-top: 1px solid #64748b;
                text-align: center;
              }
              @media print {
                body { padding: 0; }
                .sheet { max-width: none; }
              }
            </style>
          </head>
          <body>
            <div class="sheet">
              <div class="top">
                <div>
                  <h1>Advance Booking Confirmation</h1>
                  <div class="hotel">${escapeHtml(entry?.hotelName || hotelName || "Hotel")}</div>
                </div>
                <div class="badge">Bulk / Group Booking</div>
              </div>

              <div class="guest-grid">
                <div class="info">
                  <div class="label">Guest</div>
                  <div class="value">${escapeHtml(entry?.guestName || "-")}</div>
                </div>
                <div class="info">
                  <div class="label">Phone</div>
                  <div class="value">${escapeHtml(entry?.phoneNumber || "-")}</div>
                </div>
                <div class="info">
                  <div class="label">Booking Source</div>
                  <div class="value">${escapeHtml(entry?.bookingSource || "-")}</div>
                </div>
                <div class="info">
                  <div class="label">From</div>
                  <div class="value">${escapeHtml(prettyDate(entry?.checkInDate))}</div>
                </div>
                <div class="info">
                  <div class="label">To</div>
                  <div class="value">${escapeHtml(prettyDate(entry?.checkOutDate))}</div>
                </div>
                <div class="info">
                  <div class="label">Payment</div>
                  <div class="value">${escapeHtml(entry?.paymentMode || "-")}${entry?.transactionReference ? ` • ${escapeHtml(entry.transactionReference)}` : ""}</div>
                </div>
              </div>

              ${dayRows}

              <div class="money">
                <div class="money-box total">
                  <div class="label">Total Booking</div>
                  <div class="money-value">${formatMoney(totalAmount)}</div>
                </div>
                <div class="money-box advance">
                  <div class="label">Advance Received</div>
                  <div class="money-value">${formatMoney(advanceAmount)}</div>
                </div>
                <div class="money-box balance">
                  <div class="label">Balance</div>
                  <div class="money-value">${formatMoney(balanceAmount)}</div>
                </div>
              </div>

              <div class="note">
                <strong>Booking Terms:</strong><br/>
                • In any circumstances refund is not possible.<br/>
                • Rest amount must be paid at check-in.<br/>
                • Check-in time: 12:00 PM &nbsp;|&nbsp; Check-out time: 11:00 AM.<br/>
                ${entry?.extraPersonChargeEnabled && toAmount(entry?.extraPersonRate) > 0 ? `• Extra person charge: <strong>${formatMoney(entry.extraPersonRate)} per person</strong>${Number(entry?.extraPersonCount || 0) > 0 ? ` × ${Number(entry.extraPersonCount)} = <strong>${formatMoney(entry.extraPersonAmount || getBulkExtraPersonTotal(entry))}</strong>` : ""}.<br/>` : ""}
                <br/><strong>Inclusions:</strong>
                ${entry?.breakfastIncluded ? "Breakfast Included" : "Breakfast Not Included"}
                •
                ${entry?.gstIncluded ? "GST Included" : "GST Extra / As Applicable"}
                ${entry?.note ? `<br/><br/><strong>Note:</strong> ${escapeHtml(entry.note)}` : ""}
              </div>

              <div class="footer">
                <div>Generated from Hotel PMS Booking Diary</div>
                <div class="signature">Authorized Signature</div>
              </div>
            </div>
            <script>
              window.onload = function () {
                setTimeout(function () {
                  window.print();
                }, 250);
              };
            </script>
          </body>
        </html>
      `);

      printWindow.document.close();
    };

  const openEdit =
    (entry) => {
      if (
        entry?.bookingType ===
          "bulk_advance" ||
        Array.isArray(
          entry?.dailyPlan
        )
      ) {
        openBulkEdit(entry);
        return;
      }

      setForm({
        ...createEmptyForm(),
        ...entry,

        roomCount:
          String(
            entry.roomCount ?? 1
          ),

        tariff:
          entry.tariff ?? "",

        totalAmount:
          entry.totalAmount ??
          "",

        advanceAmount:
          entry.advanceAmount ??
          "",
      });

      setEditingId(entry.id);
      setModalOpen(true);
      setViewEntry(null);
    };

  const updateField = (
    field,
    value
  ) => {
    setForm(
      (current) => ({
        ...current,
        [field]: value,
      })
    );
  };

  const calculateSuggestedTotal =
    () => {
      const roomCount =
        Math.max(
          1,
          Number(
            form.roomCount || 1
          )
        );

      const tariff =
        toAmount(form.tariff);

      if (
        tariff <= 0
      ) {
        return;
      }

      let nights = 1;

      if (
        form.checkInDate &&
        form.checkOutDate
      ) {
        const inDate =
          new Date(
            `${form.checkInDate}T00:00:00`
          );

        const outDate =
          new Date(
            `${form.checkOutDate}T00:00:00`
          );

        const diff =
          Math.ceil(
            (outDate - inDate) /
              86400000
          );

        nights =
          Math.max(1, diff);
      }

      updateField(
        "totalAmount",
        String(
          roomCount *
            tariff *
            nights
        )
      );
    };

  const validate =
    () => {
      if (
        !clean(form.guestName)
      ) {
        alert(
          "Guest name required hai."
        );
        return false;
      }

      if (
        !form.checkInDate
      ) {
        alert(
          "Check-In date required hai."
        );
        return false;
      }

      if (
        form.checkOutDate &&
        form.checkOutDate <
          form.checkInDate
      ) {
        alert(
          "Checkout date Check-In se pehle nahi ho sakti."
        );
        return false;
      }

      if (
        form.paymentMode !==
          "Cash" &&
        toAmount(
          form.advanceAmount
        ) > 0 &&
        !clean(
          form.transactionReference
        )
      ) {
        alert(
          "Online advance ka transaction code enter karo."
        );
        return false;
      }

      return true;
    };

  const saveEntry =
    async () => {
      if (
        saving ||
        !validate()
      ) {
        return;
      }

      setSaving(true);

      try {
        const payload = {
          hotelId,
          hotelName:
            hotelName || "",

          guestName:
            clean(
              form.guestName
            ),

          phoneNumber:
            clean(
              form.phoneNumber
            ),

          checkInDate:
            form.checkInDate,

          checkInTime:
            form.checkInTime,

          checkOutDate:
            form.checkOutDate,

          checkOutTime:
            form.checkOutTime,

          roomCount:
            Math.max(
              1,
              Number(
                form.roomCount ||
                  1
              )
            ),

          roomPreference:
            clean(
              form.roomPreference
            ),

          occupancy:
            clean(
              form.occupancy
            ) || "Double",

          tariff:
            toAmount(
              form.tariff
            ),

          totalAmount:
            toAmount(
              form.totalAmount
            ),

          advanceAmount:
            toAmount(
              form.advanceAmount
            ),

          balanceAmount:
            Math.max(
              0,
              toAmount(
                form.totalAmount
              ) -
                toAmount(
                  form.advanceAmount
                )
            ),

          paymentMode:
            clean(
              form.paymentMode
            ) || "Cash",

          transactionReference:
            clean(
              form.transactionReference
            ),

          breakfastIncluded:
            form.breakfastIncluded ===
            true,

          gstIncluded:
            form.gstIncluded ===
            true,

          bookingSource:
            clean(
              form.bookingSource
            ) || "Direct",

          status:
            form.status ||
            "confirmed",

          note:
            clean(form.note),

          updatedAt:
            serverTimestamp(),
        };

        const bookingRef = editingId ? doc(db, 'bookingDiary', editingId) : doc(collection(db, 'bookingDiary'));
        await commitBookingDiary({database: db, reference: bookingRef, hotelId,
          payload, previous: editingId ? entries.find(entry => entry.id === editingId) : null,
          create: !editingId});

        setModalOpen(false);
        resetForm();
      } catch (error) {
        console.error(
          "Booking diary save error:",
          error
        );

        alert(
          "Booking save nahi hui: " +
            (error?.message ||
              error)
        );
      } finally {
        setSaving(false);
      }
    };

  const updateStatus =
    async (
      entry,
      status
    ) => {
      try {
        await updateDoc(
          doc(
            db,
            "bookingDiary",
            entry.id
          ),
          {
            status,
            updatedAt:
              serverTimestamp(),
          }
        );
      } catch (error) {
        alert(
          "Status update nahi hua: " +
            (error?.message ||
              error)
        );
      }
    };

  const removeEntry =
    async (entry) => {
      const ok =
        window.confirm(
          `${entry.guestName || "Booking"} ki diary entry permanently delete karni hai?`
        );

      if (!ok) return;

      try {
        await deleteDoc(
          doc(
            db,
            "bookingDiary",
            entry.id
          )
        );

        setViewEntry(null);
      } catch (error) {
        alert(
          "Booking delete nahi hui: " +
            (error?.message ||
              error)
        );
      }
    };

  return (
    <div style={pageStyle}>
      <style>{`
        @media (max-width: 760px) {
          .booking-diary-bulk-grid-fix {
            min-width: 820px;
          }
        }
      `}</style>
      <section style={headerCardStyle}>
        <div>
          <h2 style={headingStyle}>
            📔 Booking Diary
          </h2>

          <div style={subHeadingStyle}>
            Upcoming manual bookings ko simple diary format me add, edit aur track karo.
          </div>
        </div>

        <div style={headerActionsStyle}>
          <button
            type="button"
            onClick={() =>
              openBulkAdd()
            }
            style={bulkPrimaryButtonStyle}
          >
            ＋ Advance / Bulk Booking
          </button>

          <button
            type="button"
            onClick={() =>
              openAdd()
            }
            style={primaryButtonStyle}
          >
            ＋ Add Booking
          </button>
        </div>
      </section>

      <section style={summaryGridStyle}>
        <SummaryCard
          label="Today Check-In"
          value={
            todayEntries.length
          }
          note={
            prettyDate(today)
          }
          background="#eff6ff"
          color="#1d4ed8"
        />

        <SummaryCard
          label="Tomorrow Check-In"
          value={
            tomorrowEntries.length
          }
          note={
            prettyDate(tomorrow)
          }
          background="#fff7ed"
          color="#c2410c"
        />

        <SummaryCard
          label="Upcoming"
          value={
            upcomingEntries.length
          }
          note="After tomorrow"
          background="#f5f3ff"
          color="#7c3aed"
        />

        <SummaryCard
          label="Active Bookings"
          value={
            activeEntries.length
          }
          note="Confirmed + checked-in"
          background="#ecfdf5"
          color="#15803d"
        />
      </section>

      <section style={threeColumnStyle}>
        <DiarySection
          title="🔵 Today Check-In"
          subtitle={
            prettyDate(today)
          }
          entries={
            todayEntries
          }
          emptyText="Aaj koi diary check-in nahi hai."
          onAdd={() =>
            openAdd(today)
          }
          onView={
            setViewEntry
          }
          onEdit={
            openEdit
          }
        />

        <DiarySection
          title="🟠 Tomorrow Check-In"
          subtitle={
            prettyDate(
              tomorrow
            )
          }
          entries={
            tomorrowEntries
          }
          emptyText="Kal koi diary check-in nahi hai."
          onAdd={() =>
            openAdd(tomorrow)
          }
          onView={
            setViewEntry
          }
          onEdit={
            openEdit
          }
        />

        <DiarySection
          title="🟣 Upcoming Bookings"
          subtitle="Date wise"
          entries={
            upcomingEntries.slice(
              0,
              12
            )
          }
          emptyText="Upcoming booking nahi hai."
          onAdd={() =>
            openAdd()
          }
          onView={
            setViewEntry
          }
          onEdit={
            openEdit
          }
          showDate
        />
      </section>

      <section style={allBookingsCardStyle}>
        <div style={sectionTopStyle}>
          <div>
            <h3 style={sectionTitleStyle}>
              All Booking Diary Entries
            </h3>

            <div style={sectionSubStyle}>
              Search, date aur status se purani/upcoming booking dekho.
            </div>
          </div>

          <div style={filterGridStyle}>
            <input
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Guest, phone, room, note..."
              style={filterInputStyle}
            />

            <input
              type="date"
              value={dateFilter}
              onChange={(event) =>
                setDateFilter(
                  event.target.value
                )
              }
              style={filterInputStyle}
            />

            <select
              value={
                statusFilter
              }
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
              style={filterInputStyle}
            >
              <option value="active">
                Active
              </option>
              <option value="all">
                All
              </option>
              <option value="confirmed">
                Confirmed
              </option>
              <option value="checked_in">
                Checked In
              </option>
              <option value="cancelled">
                Cancelled
              </option>
              <option value="no_show">
                No Show
              </option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={emptyStyle}>
            Loading booking diary...
          </div>
        ) : filteredAll.length ===
          0 ? (
          <div style={emptyStyle}>
            Matching booking nahi mili.
          </div>
        ) : (
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>
                    Date
                  </th>
                  <th style={thStyle}>
                    Guest
                  </th>
                  <th style={thStyle}>
                    Stay
                  </th>
                  <th style={thStyle}>
                    Room / Occ.
                  </th>
                  <th style={thRightStyle}>
                    Total
                  </th>
                  <th style={thRightStyle}>
                    Advance
                  </th>
                  <th style={thRightStyle}>
                    Balance
                  </th>
                  <th style={thStyle}>
                    Status
                  </th>
                  <th style={thStyle}>
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredAll.map(
                  (entry) => (
                    <tr key={entry.id}>
                      <td style={tdStyle}>
                        <strong>
                          {prettyDate(
                            entry.checkInDate
                          )}
                        </strong>

                        <div style={tinyTextStyle}>
                          {prettyTime(
                            entry.checkInTime
                          )}
                        </div>
                      </td>

                      <td style={tdStyle}>
                        <strong>
                          {entry.guestName ||
                            "-"}
                        </strong>

                        <div style={tinyTextStyle}>
                          {entry.phoneNumber ||
                            "-"}
                        </div>
                      </td>

                      <td style={tdStyle}>
                        {prettyDate(
                          entry.checkInDate
                        )}
                        <div style={tinyTextStyle}>
                          →
                          {" "}
                          {prettyDate(
                            entry.checkOutDate
                          )}
                        </div>
                      </td>

                      <td style={tdStyle}>
                        {entry.roomCount ||
                          1}
                        {" room • "}
                        {entry.occupancy ||
                          "-"}

                        {entry.roomPreference && (
                          <div style={tinyTextStyle}>
                            {entry.roomPreference}
                          </div>
                        )}
                      </td>

                      <td style={tdRightStyle}>
                        {formatMoney(
                          entry.totalAmount
                        )}
                      </td>

                      <td style={tdRightStyle}>
                        {formatMoney(
                          entry.advanceAmount
                        )}
                      </td>

                      <td style={tdRightStyle}>
                        <strong>
                          {formatMoney(
                            getBalance(
                              entry
                            )
                          )}
                        </strong>
                      </td>

                      <td style={tdStyle}>
                        <StatusBadge
                          status={
                            entry.status
                          }
                        />
                      </td>

                      <td style={tdStyle}>
                        <div style={rowActionsStyle}>
                          <button
                            type="button"
                            onClick={() =>
                              setViewEntry(
                                entry
                              )
                            }
                            style={smallViewButton}
                          >
                            View
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              openEdit(
                                entry
                              )
                            }
                            style={smallEditButton}
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {modalOpen && (
        <div
          style={overlayStyle}
          onMouseDown={(event) => {
            if (
              event.target ===
                event.currentTarget &&
              !saving
            ) {
              setModalOpen(false);
            }
          }}
        >
          <div style={modalStyle}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>
                  {editingId
                    ? "✏ Edit Booking"
                    : "＋ Add Booking"}
                </h2>

                <div style={modalSubtitleStyle}>
                  Few-step booking diary entry
                </div>
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={() =>
                  setModalOpen(
                    false
                  )
                }
                style={closeButtonStyle}
              >
                ×
              </button>
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>
                1. Guest
              </div>

              <div style={formGridStyle}>
                <Field
                  label="Guest Name *"
                  value={
                    form.guestName
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "guestName",
                      value
                    )
                  }
                  placeholder="Guest name"
                />

                <Field
                  label="Phone"
                  value={
                    form.phoneNumber
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "phoneNumber",
                      value
                    )
                  }
                  placeholder="10-digit mobile"
                />

                <SelectField
                  label="Booking Source"
                  value={
                    form.bookingSource
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "bookingSource",
                      value
                    )
                  }
                  options={[
                    "Direct",
                    "Fab",
                    "Brevistay",
                    "Phone",
                    "WhatsApp",
                    "Other",
                  ]}
                />
              </div>
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>
                2. Stay
              </div>

              <div style={formGridStyle}>
                <Field
                  type="date"
                  label="Check-In Date *"
                  value={
                    form.checkInDate
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "checkInDate",
                      value
                    )
                  }
                />

                <Field
                  type="time"
                  label="Check-In Time"
                  value={
                    form.checkInTime
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "checkInTime",
                      value
                    )
                  }
                />

                <Field
                  type="date"
                  label="Check-Out Date"
                  value={
                    form.checkOutDate
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "checkOutDate",
                      value
                    )
                  }
                />

                <Field
                  type="time"
                  label="Check-Out Time"
                  value={
                    form.checkOutTime
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "checkOutTime",
                      value
                    )
                  }
                />

                <Field
                  type="number"
                  label="No. of Rooms"
                  value={
                    form.roomCount
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "roomCount",
                      value
                    )
                  }
                  min="1"
                />

                <SelectField
                  label="Occupancy"
                  value={
                    form.occupancy
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "occupancy",
                      value
                    )
                  }
                  options={[
                    "Single",
                    "Double",
                    "Triple",
                    "Family",
                    "Group",
                  ]}
                />

                <Field
                  label="Room No. / Preference"
                  value={
                    form.roomPreference
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "roomPreference",
                      value
                    )
                  }
                  placeholder="B9 / 5 Rooms / Suite"
                />

                <Field
                  type="number"
                  label="Tariff / Room ₹"
                  value={
                    form.tariff
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "tariff",
                      value
                    )
                  }
                  placeholder="2500"
                />
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={
                  calculateSuggestedTotal
                }
                style={calculateButtonStyle}
              >
                Calculate Total from Tariff × Rooms × Nights
              </button>
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>
                3. Payment
              </div>

              <div style={formGridStyle}>
                <Field
                  type="number"
                  label="Total Amount ₹"
                  value={
                    form.totalAmount
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "totalAmount",
                      value
                    )
                  }
                  placeholder="Total booking amount"
                />

                <Field
                  type="number"
                  label="Advance Received ₹"
                  value={
                    form.advanceAmount
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "advanceAmount",
                      value
                    )
                  }
                  placeholder="0"
                />

                <SelectField
                  label="Payment Mode"
                  value={
                    form.paymentMode
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateField(
                      "paymentMode",
                      value
                    )
                  }
                  options={[
                    "Cash",
                    "UPI",
                    "Card",
                    "Bank Transfer",
                    "OTA",
                  ]}
                />

                {form.paymentMode !==
                  "Cash" && (
                  <Field
                    label="Transaction Code"
                    value={
                      form.transactionReference
                    }
                    disabled={saving}
                    onChange={(value) =>
                      updateField(
                        "transactionReference",
                        value
                      )
                    }
                    placeholder="UTR / Code"
                  />
                )}

                {toAmount(
                  form.advanceAmount
                ) > 0 && (
                  <div style={dayBookAutoNoteStyle}>
                    📒 Advance save hote hi aaj ke Day Book → Advance & Pending Received me automatic entry banegi.
                  </div>
                )}

                <div style={balanceBoxStyle}>
                  <div style={balanceLabelStyle}>
                    Balance
                  </div>

                  <div style={balanceValueStyle}>
                    {formatMoney(
                      Math.max(
                        0,
                        toAmount(
                          form.totalAmount
                        ) -
                          toAmount(
                            form.advanceAmount
                          )
                      )
                    )}
                  </div>
                </div>
              </div>

              <div style={checkboxRowStyle}>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={
                      form.breakfastIncluded
                    }
                    disabled={saving}
                    onChange={(event) =>
                      updateField(
                        "breakfastIncluded",
                        event.target.checked
                      )
                    }
                  />
                  Breakfast Included
                </label>

                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={
                      form.gstIncluded
                    }
                    disabled={saving}
                    onChange={(event) =>
                      updateField(
                        "gstIncluded",
                        event.target.checked
                      )
                    }
                  />
                  GST Included
                </label>
              </div>
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>
                4. Note
              </div>

              <textarea
                value={form.note}
                disabled={saving}
                onChange={(event) =>
                  updateField(
                    "note",
                    event.target.value
                  )
                }
                style={textareaStyle}
                placeholder="Without BF / suite room / special request / balance due date..."
              />
            </div>

            <div style={modalFooterStyle}>
              <button
                type="button"
                disabled={saving}
                onClick={() =>
                  setModalOpen(
                    false
                  )
                }
                style={cancelButtonStyle}
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={saveEntry}
                style={saveButtonStyle}
              >
                {saving
                  ? "Saving..."
                  : editingId
                  ? "✓ Update Booking"
                  : "✓ Save Booking"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkModalOpen && (
        <div
          style={overlayStyle}
          onMouseDown={(event) => {
            if (
              event.target ===
                event.currentTarget &&
              !saving
            ) {
              setBulkModalOpen(
                false
              );
            }
          }}
        >
          <div style={bulkModalStyle}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>
                  {bulkEditingId
                    ? "✏ Edit Advance / Bulk Booking"
                    : "🏨 Advance / Bulk Booking"}
                </h2>

                <div style={modalSubtitleStyle}>
                  Ek guest ke liye date-wise multiple room categories, occupancy aur tariff.
                </div>
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={() =>
                  setBulkModalOpen(
                    false
                  )
                }
                style={closeButtonStyle}
              >
                ×
              </button>
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>
                1. Guest / Booking
              </div>

              <div style={formGridStyle}>
                <Field
                  label="Guest Name *"
                  value={
                    bulkForm.guestName
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateBulkField(
                      "guestName",
                      value
                    )
                  }
                  placeholder="Guest / Group name"
                />

                <Field
                  label="Phone"
                  value={
                    bulkForm.phoneNumber
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateBulkField(
                      "phoneNumber",
                      value
                    )
                  }
                  placeholder="Mobile number"
                />

                <SelectField
                  label="Booking Source"
                  value={
                    bulkForm.bookingSource
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateBulkField(
                      "bookingSource",
                      value
                    )
                  }
                  options={[
                    "Direct",
                    "Fab",
                    "Brevistay",
                    "Phone",
                    "WhatsApp",
                    "Other",
                  ]}
                />

                <Field
                  type="time"
                  label="Check-In Time"
                  value={
                    bulkForm.checkInTime
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateBulkField(
                      "checkInTime",
                      value
                    )
                  }
                />

                <Field
                  type="time"
                  label="Check-Out Time"
                  value={
                    bulkForm.checkOutTime
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateBulkField(
                      "checkOutTime",
                      value
                    )
                  }
                />
              </div>
            </div>

            <div style={formSectionStyle}>
              <div style={bulkSectionTopStyle}>
                <div>
                  <div style={formSectionTitleStyle}>
                    2. Day-wise Room Plan
                  </div>
                  <div style={bulkHelpStyle}>
                    Har date par jitni room categories chahiye add karo. Same rooms next day continue hain to “Copy Previous Day” use karo.
                  </div>
                </div>

                <div style={bulkTopActionsStyle}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={
                      duplicatePreviousBulkDay
                    }
                    style={copyDayButtonStyle}
                  >
                    📋 Copy Previous Day
                  </button>

                  <button
                    type="button"
                    disabled={saving}
                    onClick={addBulkDay}
                    style={addDayButtonStyle}
                  >
                    ＋ Add Day
                  </button>
                </div>
              </div>

              <div style={bulkDaysWrapStyle}>
                {bulkForm.days.map(
                  (day, dayIndex) => (
                    <div
                      key={`bulk-day-${dayIndex}`}
                      style={bulkDayCardStyle}
                    >
                      <div style={bulkDayHeaderStyle}>
                        <div style={bulkDateWrapStyle}>
                          <strong>
                            Day {dayIndex + 1}
                          </strong>

                          <input
                            type="date"
                            value={
                              day.date || ""
                            }
                            disabled={saving}
                            onChange={(event) =>
                              updateBulkDayDate(
                                dayIndex,
                                event.target.value
                              )
                            }
                            style={bulkDateInputStyle}
                          />
                        </div>

                        <div style={bulkDayHeaderRightStyle}>
                          <span style={bulkDayTotalStyle}>
                            Day Total:{" "}
                            {formatMoney(
                              getBulkDayTotal(
                                day
                              )
                            )}
                          </span>

                          {bulkForm.days.length > 1 && (
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() =>
                                removeBulkDay(
                                  dayIndex
                                )
                              }
                              style={removeDayButtonStyle}
                            >
                              Remove Day
                            </button>
                          )}
                        </div>
                      </div>

                      <div style={bulkGroupHeaderStyle}>
                        <span>Room Type</span>
                        <span>Occupancy</span>
                        <span>Rooms</span>
                        <span>Tariff / Room</span>
                        <span>Line Total</span>
                        <span></span>
                      </div>

                      {(day.groups || []).map(
                        (
                          group,
                          groupIndex
                        ) => (
                          <div
                            key={`bulk-group-${dayIndex}-${groupIndex}`}
                            style={bulkGroupRowStyle}
                          >
                            <input
                              value={
                                group.roomType ||
                                ""
                              }
                              disabled={saving}
                              placeholder="Suite / Executive / Deluxe"
                              onChange={(event) =>
                                updateBulkGroup(
                                  dayIndex,
                                  groupIndex,
                                  "roomType",
                                  event.target.value
                                )
                              }
                              style={inputStyle}
                            />

                            <select
                              value={
                                group.occupancy ||
                                "Double"
                              }
                              disabled={saving}
                              onChange={(event) =>
                                updateBulkGroup(
                                  dayIndex,
                                  groupIndex,
                                  "occupancy",
                                  event.target.value
                                )
                              }
                              style={inputStyle}
                            >
                              <option value="Single">
                                Single
                              </option>
                              <option value="Double">
                                Double
                              </option>
                              <option value="Triple">
                                Triple
                              </option>
                            </select>

                            <input
                              type="number"
                              min="1"
                              value={
                                group.roomCount ??
                                "1"
                              }
                              disabled={saving}
                              onChange={(event) =>
                                updateBulkGroup(
                                  dayIndex,
                                  groupIndex,
                                  "roomCount",
                                  event.target.value
                                )
                              }
                              style={inputStyle}
                            />

                            <input
                              type="number"
                              min="0"
                              value={
                                group.tariff ??
                                ""
                              }
                              disabled={saving}
                              placeholder="4800"
                              onChange={(event) =>
                                updateBulkGroup(
                                  dayIndex,
                                  groupIndex,
                                  "tariff",
                                  event.target.value
                                )
                              }
                              style={inputStyle}
                            />

                            <div style={bulkLineTotalStyle}>
                              {formatMoney(
                                Math.max(
                                  1,
                                  Number(
                                    group.roomCount ||
                                      1
                                  )
                                ) *
                                  toAmount(
                                    group.tariff
                                  )
                              )}
                            </div>

                            <button
                              type="button"
                              disabled={saving}
                              onClick={() =>
                                removeBulkGroup(
                                  dayIndex,
                                  groupIndex
                                )
                              }
                              style={bulkRemoveGroupButtonStyle}
                            >
                              ×
                            </button>
                          </div>
                        )
                      )}

                      <button
                        type="button"
                        disabled={saving}
                        onClick={() =>
                          addBulkGroup(
                            dayIndex
                          )
                        }
                        style={addRoomTypeButtonStyle}
                      >
                        ＋ Add Room Type
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>
                3. Booking Amount / Advance
              </div>

              <div style={extraPersonSetupWrapStyle}>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    updateBulkField(
                      "extraPersonChargeEnabled",
                      !bulkForm.extraPersonChargeEnabled
                    )
                  }
                  style={extraPersonToggleButtonStyle}
                >
                  {bulkForm.extraPersonChargeEnabled
                    ? "✓ Extra Person Charge Set"
                    : "＋ Set Extra Person Charge"}
                </button>

                {bulkForm.extraPersonChargeEnabled && (
                  <div style={extraPersonFieldsStyle}>
                    <Field
                      type="number"
                      label="Extra Person Charge / Person ₹"
                      value={bulkForm.extraPersonRate}
                      disabled={saving}
                      onChange={(value) =>
                        updateBulkField(
                          "extraPersonRate",
                          value
                        )
                      }
                      placeholder="500 / 800"
                      min="0"
                    />

                    <Field
                      type="number"
                      label="Extra Persons (if confirmed)"
                      value={bulkForm.extraPersonCount}
                      disabled={saving}
                      onChange={(value) =>
                        updateBulkField(
                          "extraPersonCount",
                          value
                        )
                      }
                      placeholder="0"
                      min="0"
                    />

                    <div style={extraPersonAmountBoxStyle}>
                      <div style={balanceLabelStyle}>
                        Extra Person Amount
                      </div>
                      <div style={bulkMoneyValueStyle}>
                        {formatMoney(
                          getBulkExtraPersonTotal(
                            bulkForm
                          )
                        )}
                      </div>
                      <div style={bulkHelpStyle}>
                        Count 0 rehne par sirf per-person rate confirmation/PDF me show hoga.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={bulkPolicyBoxStyle}>
                <strong>Booking Terms</strong>
                <div>• In any circumstances refund is not possible.</div>
                <div>• Rest amount check-in time par dena hai.</div>
                <div>• Check-in: 12:00 PM • Check-out: 11:00 PM</div>
              </div>

              <div style={bulkMoneySummaryStyle}>
                <div
                  style={{
                    ...bulkMoneyCardStyle,
                    background:
                      "#eff6ff",
                  }}
                >
                  <div style={balanceLabelStyle}>
                    Total Booking
                  </div>
                  <div
                    style={{
                      ...bulkMoneyValueStyle,
                      color: "#1d4ed8",
                    }}
                  >
                    {formatMoney(
                      getBulkGrandTotal(
                        bulkForm
                      )
                    )}
                  </div>
                </div>

                <div
                  style={{
                    ...bulkMoneyCardStyle,
                    background:
                      "#ecfdf5",
                  }}
                >
                  <div style={balanceLabelStyle}>
                    Advance
                  </div>
                  <div
                    style={{
                      ...bulkMoneyValueStyle,
                      color: "#15803d",
                    }}
                  >
                    {formatMoney(
                      bulkForm.advanceAmount
                    )}
                  </div>
                </div>

                <div
                  style={{
                    ...bulkMoneyCardStyle,
                    background:
                      "#fff1f2",
                  }}
                >
                  <div style={balanceLabelStyle}>
                    Balance
                  </div>
                  <div
                    style={{
                      ...bulkMoneyValueStyle,
                      color: "#be123c",
                    }}
                  >
                    {formatMoney(
                      Math.max(
                        0,
                        getBulkGrandTotal(
                          bulkForm
                        ) -
                          toAmount(
                            bulkForm.advanceAmount
                          )
                      )
                    )}
                  </div>
                </div>
              </div>

              <div
                style={{
                  ...formGridStyle,
                  marginTop: 12,
                }}
              >
                <Field
                  type="number"
                  label="Advance Received ₹"
                  value={
                    bulkForm.advanceAmount
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateBulkField(
                      "advanceAmount",
                      value
                    )
                  }
                  placeholder="40000"
                />

                <SelectField
                  label="Payment Mode"
                  value={
                    bulkForm.paymentMode
                  }
                  disabled={saving}
                  onChange={(value) =>
                    updateBulkField(
                      "paymentMode",
                      value
                    )
                  }
                  options={[
                    "Cash",
                    "UPI",
                    "Card",
                    "Bank Transfer",
                    "OTA",
                  ]}
                />

                {bulkForm.paymentMode !==
                  "Cash" && (
                  <Field
                    label="Transaction Code"
                    value={
                      bulkForm.transactionReference
                    }
                    disabled={saving}
                    onChange={(value) =>
                      updateBulkField(
                        "transactionReference",
                        value
                      )
                    }
                    placeholder="UTR / Code"
                  />
                )}
              </div>

              {toAmount(
                bulkForm.advanceAmount
              ) > 0 && (
                <div style={dayBookAutoNoteStyle}>
                  📒 Advance save hote hi Day Book → Advance & Pending Received me automatic entry banegi.
                </div>
              )}

              <div style={checkboxRowStyle}>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={
                      bulkForm.breakfastIncluded
                    }
                    disabled={saving}
                    onChange={(event) =>
                      updateBulkField(
                        "breakfastIncluded",
                        event.target.checked
                      )
                    }
                  />
                  Breakfast Included
                </label>

                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={
                      bulkForm.gstIncluded
                    }
                    disabled={saving}
                    onChange={(event) =>
                      updateBulkField(
                        "gstIncluded",
                        event.target.checked
                      )
                    }
                  />
                  GST Included
                </label>
              </div>
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>
                4. Note
              </div>

              <textarea
                value={bulkForm.note}
                disabled={saving}
                onChange={(event) =>
                  updateBulkField(
                    "note",
                    event.target.value
                  )
                }
                style={textareaStyle}
                placeholder="Breakfast timing / room service / special request / cancellation terms..."
              />
            </div>

            <div style={modalFooterStyle}>
              <button
                type="button"
                disabled={saving}
                onClick={() =>
                  setBulkModalOpen(
                    false
                  )
                }
                style={cancelButtonStyle}
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={saveBulkEntry}
                style={saveButtonStyle}
              >
                {saving
                  ? "Saving..."
                  : bulkEditingId
                  ? "✓ Update Bulk Booking"
                  : "✓ Save Advance Booking"}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewEntry && (
        <div
          style={overlayStyle}
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setViewEntry(null);
            }
          }}
        >
          <div style={viewModalStyle}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>
                  {viewEntry.guestName ||
                    "Booking"}
                </h2>

                <div style={modalSubtitleStyle}>
                  {prettyDate(
                    viewEntry.checkInDate
                  )}
                  {" • "}
                  {prettyTime(
                    viewEntry.checkInTime
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  setViewEntry(null)
                }
                style={closeButtonStyle}
              >
                ×
              </button>
            </div>

            {(
              viewEntry.bookingType ===
                "bulk_advance" ||
              Array.isArray(
                viewEntry.dailyPlan
              )
            ) && (
              <div style={bulkViewBlockStyle}>
                <div style={bulkViewTitleStyle}>
                  🏨 Day-wise Bulk Booking Plan
                </div>

                <BulkBookingBreakdown
                  days={
                    viewEntry.dailyPlan ||
                    []
                  }
                />
              </div>
            )}

            <div style={viewSummaryGridStyle}>
              <ViewItem
                label="Phone"
                value={
                  viewEntry.phoneNumber ||
                  "-"
                }
              />

              <ViewItem
                label="Check-In"
                value={`${prettyDate(
                  viewEntry.checkInDate
                )} • ${prettyTime(
                  viewEntry.checkInTime
                )}`}
              />

              <ViewItem
                label="Check-Out"
                value={`${prettyDate(
                  viewEntry.checkOutDate
                )} • ${prettyTime(
                  viewEntry.checkOutTime
                )}`}
              />

              <ViewItem
                label="Rooms"
                value={`${viewEntry.roomCount || 1} • ${viewEntry.occupancy || "-"}`}
              />

              <ViewItem
                label="Room / Preference"
                value={
                  viewEntry.roomPreference ||
                  "-"
                }
              />

              <ViewItem
                label="Tariff"
                value={
                  viewEntry.bookingType ===
                    "bulk_advance" ||
                  Array.isArray(
                    viewEntry.dailyPlan
                  )
                    ? "Day-wise / Category-wise"
                    : formatMoney(
                        viewEntry.tariff
                      )
                }
              />

              <ViewItem
                label="Total"
                value={formatMoney(
                  viewEntry.totalAmount
                )}
              />

              <ViewItem
                label="Advance"
                value={formatMoney(
                  viewEntry.advanceAmount
                )}
              />

              <ViewItem
                label="Balance"
                value={formatMoney(
                  getBalance(
                    viewEntry
                  )
                )}
                danger={
                  getBalance(
                    viewEntry
                  ) > 0
                }
              />

              {(viewEntry.extraPersonChargeEnabled === true || toAmount(viewEntry.extraPersonRate) > 0) && (
                <ViewItem
                  label="Extra Person Charge"
                  value={`${formatMoney(viewEntry.extraPersonRate)} / person${Number(viewEntry.extraPersonCount || 0) > 0 ? ` • ${Number(viewEntry.extraPersonCount)} person • ${formatMoney(viewEntry.extraPersonAmount || getBulkExtraPersonTotal(viewEntry))}` : ""}`}
                />
              )}

              <ViewItem
                label="Payment"
                value={`${viewEntry.paymentMode || "-"}${
                  viewEntry.transactionReference
                    ? ` • ${viewEntry.transactionReference}`
                    : ""
                }`}
              />

              <ViewItem
                label="Source"
                value={
                  viewEntry.bookingSource ||
                  "-"
                }
              />

              <ViewItem
                label="Status"
                value={statusLabel(
                  viewEntry.status
                )}
              />
            </div>

            <div style={inclusionRowStyle}>
              <span>
                {viewEntry.breakfastIncluded
                  ? "✅"
                  : "❌"}
                {" "}
                Breakfast
              </span>

              <span>
                {viewEntry.gstIncluded
                  ? "✅"
                  : "❌"}
                {" "}
                GST Included
              </span>
            </div>

            {viewEntry.note && (
              <div style={noteBoxStyle}>
                <strong>Note</strong>
                <div style={{ marginTop: 4 }}>
                  {viewEntry.note}
                </div>
              </div>
            )}

            <div style={viewActionsStyle}>
              {(
                viewEntry.bookingType ===
                  "bulk_advance" ||
                Array.isArray(
                  viewEntry.dailyPlan
                )
              ) && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      printBulkBooking(
                        viewEntry
                      )
                    }
                    style={printButtonStyle}
                  >
                    🖨 Print / Save PDF
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      downloadBulkBookingPdf(
                        viewEntry
                      )
                    }
                    style={downloadPdfButtonStyle}
                  >
                    ⬇ Download PDF
                  </button>
                </>
              )}

              <button
                type="button"
                onClick={() =>
                  openEdit(
                    viewEntry
                  )
                }
                style={smallEditButton}
              >
                ✏ Edit
              </button>

              {viewEntry.status ===
                "confirmed" && (
                <button
                  type="button"
                  onClick={() =>
                    updateStatus(
                      viewEntry,
                      "checked_in"
                    )
                  }
                  style={checkedInButtonStyle}
                >
                  ✓ Mark Checked-In
                </button>
              )}

              {![
                "cancelled",
                "checked_in",
              ].includes(
                viewEntry.status
              ) && (
                <button
                  type="button"
                  onClick={() =>
                    updateStatus(
                      viewEntry,
                      "cancelled"
                    )
                  }
                  style={cancelBookingButtonStyle}
                >
                  Cancel Booking
                </button>
              )}

              <button
                type="button"
                onClick={() =>
                  removeEntry(
                    viewEntry
                  )
                }
                style={deleteButtonStyle}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Sub Components                               */
/* -------------------------------------------------------------------------- */

function SummaryCard({
  label,
  value,
  note,
  background,
  color,
}) {
  return (
    <div
      style={{
        ...summaryCardStyle,
        background,
      }}
    >
      <div style={summaryLabelStyle}>
        {label}
      </div>

      <div
        style={{
          ...summaryValueStyle,
          color,
        }}
      >
        {value}
      </div>

      <div style={summaryNoteStyle}>
        {note}
      </div>
    </div>
  );
}

function DiarySection({
  title,
  subtitle,
  entries,
  emptyText,
  onAdd,
  onView,
  onEdit,
  showDate = false,
}) {
  return (
    <section style={diaryCardStyle}>
      <div style={diaryHeaderStyle}>
        <div>
          <h3 style={diaryTitleStyle}>
            {title}
          </h3>

          <div style={sectionSubStyle}>
            {subtitle}
          </div>
        </div>

        <button
          type="button"
          onClick={onAdd}
          style={miniAddButtonStyle}
        >
          ＋
        </button>
      </div>

      <div style={diaryBodyStyle}>
        {entries.length === 0 ? (
          <div style={diaryEmptyStyle}>
            {emptyText}
          </div>
        ) : (
          entries.map(
            (entry) => (
              <div
                key={entry.id}
                style={bookingRowStyle}
              >
                <div
                  onClick={() =>
                    onView(entry)
                  }
                  style={{
                    cursor: "pointer",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <div style={bookingMainLineStyle}>
                    {entry.guestName ||
                      "Guest"}

                    <StatusBadge
                      status={
                        entry.status
                      }
                    />
                  </div>

                  <div style={bookingMetaStyle}>
                    {showDate && (
                      <span>
                        📅{" "}
                        {prettyDate(
                          entry.checkInDate
                        )}
                      </span>
                    )}

                    <span>
                      🕒{" "}
                      {prettyTime(
                        entry.checkInTime
                      )}
                    </span>

                    <span>
                      🛏{" "}
                      {entry.roomCount ||
                        1}
                      {" • "}
                      {entry.occupancy ||
                        "-"}
                    </span>
                  </div>

                  <div style={bookingAmountsStyle}>
                    <span>
                      Total{" "}
                      <strong>
                        {formatMoney(
                          entry.totalAmount
                        )}
                      </strong>
                    </span>

                    <span>
                      Adv{" "}
                      <strong>
                        {formatMoney(
                          entry.advanceAmount
                        )}
                      </strong>
                    </span>

                    <span>
                      Bal{" "}
                      <strong>
                        {formatMoney(
                          getBalance(
                            entry
                          )
                        )}
                      </strong>
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    onEdit(entry)
                  }
                  style={inlineEditButtonStyle}
                >
                  ✏
                </button>
              </div>
            )
          )
        )}
      </div>
    </section>
  );
}

function BulkBookingBreakdown({
  days = [],
}) {
  const normalizedDays =
    normalizeBulkDays(days);

  if (
    normalizedDays.length === 0
  ) {
    return (
      <div style={bulkEmptyStyle}>
        Day-wise room plan available nahi hai.
      </div>
    );
  }

  return (
    <div style={bulkBreakdownWrapStyle}>
      {normalizedDays.map(
        (day) => (
          <div
            key={day.date}
            style={bulkBreakdownDayStyle}
          >
            <div style={bulkBreakdownDayHeaderStyle}>
              <strong>
                {prettyDate(
                  day.date
                )}
              </strong>

              <strong>
                {formatMoney(
                  getBulkDayTotal(
                    day
                  )
                )}
              </strong>
            </div>

            <div style={bulkBreakdownRowsStyle}>
              {day.groups.map(
                (
                  group,
                  groupIndex
                ) => (
                  <div
                    key={`${day.date}-${groupIndex}`}
                    style={bulkBreakdownRowStyle}
                  >
                    <div>
                      <strong>
                        {group.roomCount} × {group.roomType}
                      </strong>
                      <div style={tinyTextStyle}>
                        {group.occupancy} Occupancy
                      </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div>
                        {formatMoney(
                          group.tariff
                        )} / room
                      </div>
                      <strong>
                        {formatMoney(
                          group.roomCount *
                            group.tariff
                        )}
                      </strong>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
}

function StatusBadge({
  status,
}) {
  const style =
    statusStyle(status);

  return (
    <span
      style={{
        ...statusBadgeStyle,
        ...style,
      }}
    >
      {statusLabel(status)}
    </span>
  );
}

function Field({
  label,
  value,
  onChange,
  disabled,
  type = "text",
  placeholder = "",
  min,
}) {
  return (
    <label>
      <div style={fieldLabelStyle}>
        {label}
      </div>

      <input
        type={type}
        value={value ?? ""}
        disabled={disabled}
        placeholder={placeholder}
        min={min}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        style={inputStyle}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  disabled,
  options,
}) {
  return (
    <label>
      <div style={fieldLabelStyle}>
        {label}
      </div>

      <select
        value={value}
        disabled={disabled}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        style={inputStyle}
      >
        {options.map(
          (option) => (
            <option
              key={option}
              value={option}
            >
              {option}
            </option>
          )
        )}
      </select>
    </label>
  );
}

function ViewItem({
  label,
  value,
  danger = false,
}) {
  return (
    <div style={viewItemStyle}>
      <div style={viewLabelStyle}>
        {label}
      </div>

      <div
        style={{
          ...viewValueStyle,
          color: danger
            ? "#dc2626"
            : "#0f172a",
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Styles                                    */
/* -------------------------------------------------------------------------- */

const pageStyle = {
  display: "grid",
  gap: 14,
};

const headerCardStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  padding: 16,
  border: "1px solid #dbe3ef",
  borderRadius: 14,
  background: "#ffffff",
};

const headingStyle = {
  margin: 0,
  color: "#0f172a",
  fontSize: 22,
};

const subHeadingStyle = {
  marginTop: 5,
  color: "#64748b",
  fontSize: 11,
};

const headerActionsStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "center",
};

const bulkPrimaryButtonStyle = {
  border: 0,
  borderRadius: 10,
  padding: "11px 16px",
  background: "#7c3aed",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const primaryButtonStyle = {
  border: 0,
  borderRadius: 10,
  padding: "11px 16px",
  background: "#2563eb",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(150px,1fr))",
  gap: 10,
};

const summaryCardStyle = {
  padding: 14,
  border: "1px solid #dbe3ef",
  borderRadius: 12,
};

const summaryLabelStyle = {
  color: "#475569",
  fontSize: 10,
  fontWeight: 900,
};

const summaryValueStyle = {
  marginTop: 5,
  fontSize: 24,
  fontWeight: 900,
};

const summaryNoteStyle = {
  marginTop: 3,
  color: "#64748b",
  fontSize: 9,
};

const threeColumnStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(310px,1fr))",
  gap: 12,
  alignItems: "start",
};

const diaryCardStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 13,
  background: "#ffffff",
  overflow: "hidden",
};

const diaryHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  padding: 12,
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
};

const diaryTitleStyle = {
  margin: 0,
  fontSize: 14,
};

const miniAddButtonStyle = {
  width: 32,
  height: 32,
  border: 0,
  borderRadius: 8,
  background: "#2563eb",
  color: "#ffffff",
  fontSize: 18,
  fontWeight: 900,
  cursor: "pointer",
};

const diaryBodyStyle = {
  maxHeight: 420,
  overflowY: "auto",
};

const diaryEmptyStyle = {
  padding: 26,
  textAlign: "center",
  color: "#94a3b8",
  fontSize: 10,
};

const bookingRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: 11,
  borderBottom: "1px solid #e2e8f0",
};

const bookingMainLineStyle = {
  display: "flex",
  gap: 7,
  alignItems: "center",
  flexWrap: "wrap",
  color: "#111827",
  fontSize: 12,
  fontWeight: 900,
};

const bookingMetaStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  marginTop: 5,
  color: "#64748b",
  fontSize: 9,
};

const bookingAmountsStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 6,
  color: "#334155",
  fontSize: 9,
};

const inlineEditButtonStyle = {
  width: 31,
  height: 31,
  border: "1px solid #bfdbfe",
  borderRadius: 8,
  background: "#eff6ff",
  color: "#1d4ed8",
  cursor: "pointer",
};

const allBookingsCardStyle = {
  border: "1px solid #dbe3ef",
  borderRadius: 14,
  background: "#ffffff",
  overflow: "hidden",
};

const sectionTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  padding: 14,
  borderBottom: "1px solid #e2e8f0",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: 15,
};

const sectionSubStyle = {
  marginTop: 3,
  color: "#64748b",
  fontSize: 9,
};

const filterGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "minmax(180px,1fr) 145px 140px",
  gap: 7,
  flex: "1 1 540px",
  maxWidth: 650,
};

const filterInputStyle = {
  width: "100%",
  minWidth: 0,
  padding: "9px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  boxSizing: "border-box",
  background: "#ffffff",
};

const tableWrapStyle = {
  overflowX: "auto",
};

const tableStyle = {
  width: "100%",
  minWidth: 1050,
  borderCollapse: "collapse",
};

const thStyle = {
  padding: 10,
  background: "#e2e8f0",
  borderBottom: "1px solid #94a3b8",
  color: "#0f172a",
  fontSize: 9,
  textAlign: "left",
};

const thRightStyle = {
  ...thStyle,
  textAlign: "right",
};

const tdStyle = {
  padding: 10,
  borderBottom: "1px solid #e2e8f0",
  color: "#334155",
  fontSize: 9,
  verticalAlign: "top",
};

const tdRightStyle = {
  ...tdStyle,
  textAlign: "right",
};

const tinyTextStyle = {
  marginTop: 3,
  color: "#64748b",
  fontSize: 8,
};

const statusBadgeStyle = {
  display: "inline-flex",
  padding: "4px 7px",
  borderRadius: 999,
  fontSize: 8,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const rowActionsStyle = {
  display: "flex",
  gap: 5,
};

const smallViewButton = {
  border: "1px solid #cbd5e1",
  borderRadius: 7,
  padding: "6px 8px",
  background: "#ffffff",
  color: "#334155",
  fontWeight: 800,
  cursor: "pointer",
};

const smallEditButton = {
  border: 0,
  borderRadius: 7,
  padding: "6px 9px",
  background: "#2563eb",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const emptyStyle = {
  padding: 28,
  textAlign: "center",
  color: "#94a3b8",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 32000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 14,
  background: "rgba(15,23,42,.78)",
};

const modalStyle = {
  width: "min(980px,100%)",
  maxHeight: "95vh",
  overflowY: "auto",
  borderRadius: 18,
  background: "#ffffff",
  padding: 18,
};

const viewModalStyle = {
  width: "min(760px,100%)",
  maxHeight: "94vh",
  overflowY: "auto",
  borderRadius: 18,
  background: "#ffffff",
  padding: 18,
};

const modalHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 10,
  marginBottom: 13,
};

const modalTitleStyle = {
  margin: 0,
  fontSize: 20,
};

const modalSubtitleStyle = {
  marginTop: 4,
  color: "#64748b",
  fontSize: 10,
};

const closeButtonStyle = {
  width: 36,
  height: 36,
  border: 0,
  borderRadius: 9,
  background: "#f1f5f9",
  fontSize: 20,
  fontWeight: 900,
  cursor: "pointer",
};

const formSectionStyle = {
  marginTop: 10,
  padding: 13,
  border: "1px solid #e2e8f0",
  borderRadius: 11,
  background: "#f8fafc",
};

const formSectionTitleStyle = {
  marginBottom: 10,
  color: "#0f172a",
  fontSize: 12,
  fontWeight: 900,
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(180px,1fr))",
  gap: 10,
};

const fieldLabelStyle = {
  marginBottom: 5,
  color: "#475569",
  fontSize: 9,
  fontWeight: 900,
};

const inputStyle = {
  width: "100%",
  padding: "9px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  boxSizing: "border-box",
  background: "#ffffff",
};

const calculateButtonStyle = {
  marginTop: 10,
  border: "1px solid #93c5fd",
  borderRadius: 8,
  padding: "8px 11px",
  background: "#eff6ff",
  color: "#1d4ed8",
  fontSize: 9,
  fontWeight: 900,
  cursor: "pointer",
};

const dayBookAutoNoteStyle = {
  gridColumn: "1 / -1",
  padding: "8px 10px",
  border: "1px solid #bfdbfe",
  borderRadius: 8,
  background: "#eff6ff",
  color: "#1d4ed8",
  fontSize: 9,
  fontWeight: 800,
};

const balanceBoxStyle = {
  padding: 10,
  border: "1px solid #fecaca",
  borderRadius: 8,
  background: "#fff7f7",
};

const balanceLabelStyle = {
  color: "#64748b",
  fontSize: 9,
  fontWeight: 800,
};

const balanceValueStyle = {
  marginTop: 3,
  color: "#dc2626",
  fontSize: 18,
  fontWeight: 900,
};

const checkboxRowStyle = {
  display: "flex",
  gap: 16,
  flexWrap: "wrap",
  marginTop: 11,
};

const checkboxLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#334155",
  fontSize: 10,
  fontWeight: 800,
};

const textareaStyle = {
  width: "100%",
  minHeight: 80,
  padding: 10,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  boxSizing: "border-box",
  resize: "vertical",
};

const modalFooterStyle = {
  position: "sticky",
  bottom: -18,
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  margin: "14px -18px -18px",
  padding: "12px 18px",
  borderTop: "1px solid #e2e8f0",
  background: "rgba(255,255,255,.98)",
};

const cancelButtonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "9px 13px",
  background: "#ffffff",
  color: "#475569",
  fontWeight: 800,
  cursor: "pointer",
};

const saveButtonStyle = {
  border: 0,
  borderRadius: 8,
  padding: "9px 14px",
  background: "#16a34a",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const viewSummaryGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(190px,1fr))",
  gap: 8,
};

const viewItemStyle = {
  padding: 10,
  border: "1px solid #e2e8f0",
  borderRadius: 9,
  background: "#f8fafc",
};

const viewLabelStyle = {
  color: "#64748b",
  fontSize: 8,
  fontWeight: 900,
};

const viewValueStyle = {
  marginTop: 4,
  fontSize: 11,
  fontWeight: 900,
};

const inclusionRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 11,
  padding: 10,
  borderRadius: 9,
  background: "#f8fafc",
  color: "#334155",
  fontSize: 10,
  fontWeight: 800,
};

const noteBoxStyle = {
  marginTop: 10,
  padding: 11,
  borderRadius: 9,
  background: "#fffbeb",
  color: "#713f12",
  fontSize: 10,
};

const viewActionsStyle = {
  display: "flex",
  gap: 7,
  flexWrap: "wrap",
  justifyContent: "flex-end",
  marginTop: 13,
};

const bulkModalStyle = {
  width: "min(1180px,100%)",
  maxHeight: "95vh",
  overflowY: "auto",
  borderRadius: 18,
  background: "#ffffff",
  padding: 18,
};

const bulkSectionTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "flex-start",
  flexWrap: "wrap",
};

const bulkHelpStyle = {
  marginTop: -5,
  maxWidth: 620,
  color: "#64748b",
  fontSize: 9,
  lineHeight: 1.5,
};

const bulkTopActionsStyle = {
  display: "flex",
  gap: 7,
  flexWrap: "wrap",
};

const copyDayButtonStyle = {
  border: "1px solid #c4b5fd",
  borderRadius: 8,
  padding: "8px 10px",
  background: "#f5f3ff",
  color: "#6d28d9",
  fontSize: 9,
  fontWeight: 900,
  cursor: "pointer",
};

const addDayButtonStyle = {
  border: 0,
  borderRadius: 8,
  padding: "8px 11px",
  background: "#7c3aed",
  color: "#ffffff",
  fontSize: 9,
  fontWeight: 900,
  cursor: "pointer",
};

const bulkDaysWrapStyle = {
  display: "grid",
  gap: 11,
  marginTop: 12,
};

const bulkDayCardStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 11,
  background: "#ffffff",
  overflowX: "auto",
  overflowY: "hidden",
};

const bulkDayHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  padding: 10,
  background: "#eef2ff",
  borderBottom: "1px solid #c7d2fe",
};

const bulkDateWrapStyle = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  flexWrap: "wrap",
  fontSize: 11,
};

const bulkDateInputStyle = {
  padding: "7px 9px",
  border: "1px solid #cbd5e1",
  borderRadius: 7,
  background: "#ffffff",
};

const bulkDayHeaderRightStyle = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};

const bulkDayTotalStyle = {
  color: "#312e81",
  fontSize: 11,
  fontWeight: 900,
};

const removeDayButtonStyle = {
  border: "1px solid #fecaca",
  borderRadius: 7,
  padding: "6px 8px",
  background: "#fff1f2",
  color: "#be123c",
  fontSize: 8,
  fontWeight: 900,
  cursor: "pointer",
};

const bulkGroupHeaderStyle = {
  display: "grid",
  minWidth: 780,
  gridTemplateColumns:
    "1.35fr 1fr .7fr 1fr 1fr 36px",
  gap: 8,
  padding: "8px 10px 5px",
  color: "#64748b",
  fontSize: 8,
  fontWeight: 900,
};

const bulkGroupRowStyle = {
  display: "grid",
  minWidth: 780,
  gridTemplateColumns:
    "1.35fr 1fr .7fr 1fr 1fr 36px",
  gap: 8,
  alignItems: "center",
  padding: "5px 10px",
};

const bulkLineTotalStyle = {
  padding: "9px 7px",
  borderRadius: 7,
  background: "#f8fafc",
  color: "#0f172a",
  textAlign: "right",
  fontSize: 10,
  fontWeight: 900,
};

const bulkRemoveGroupButtonStyle = {
  width: 34,
  height: 34,
  border: 0,
  borderRadius: 8,
  background: "#fee2e2",
  color: "#b91c1c",
  fontSize: 17,
  fontWeight: 900,
  cursor: "pointer",
};

const addRoomTypeButtonStyle = {
  margin: "7px 10px 10px",
  border: "1px dashed #93c5fd",
  borderRadius: 8,
  padding: "7px 10px",
  background: "#eff6ff",
  color: "#1d4ed8",
  fontSize: 9,
  fontWeight: 900,
  cursor: "pointer",
};

const extraPersonSetupWrapStyle = {
  marginBottom: 12,
};

const extraPersonToggleButtonStyle = {
  border: "1px solid #f59e0b",
  borderRadius: 9,
  padding: "9px 12px",
  background: "#fffbeb",
  color: "#b45309",
  fontSize: 9,
  fontWeight: 900,
  cursor: "pointer",
};

const extraPersonFieldsStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
  gap: 9,
  marginTop: 9,
  padding: 10,
  border: "1px solid #fde68a",
  borderRadius: 10,
  background: "#fffdf5",
};

const extraPersonAmountBoxStyle = {
  padding: 10,
  border: "1px solid #fed7aa",
  borderRadius: 9,
  background: "#fff7ed",
};

const bulkPolicyBoxStyle = {
  marginBottom: 12,
  padding: 10,
  border: "1px solid #fecaca",
  borderRadius: 10,
  background: "#fff1f2",
  color: "#9f1239",
  fontSize: 9,
  lineHeight: 1.6,
};

const bulkMoneySummaryStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(170px,1fr))",
  gap: 9,
};

const bulkMoneyCardStyle = {
  padding: 12,
  border: "1px solid #dbe3ef",
  borderRadius: 10,
};

const bulkMoneyValueStyle = {
  marginTop: 4,
  fontSize: 20,
  fontWeight: 900,
};

const bulkViewBlockStyle = {
  marginBottom: 12,
  padding: 11,
  border: "1px solid #c4b5fd",
  borderRadius: 11,
  background: "#faf5ff",
};

const bulkViewTitleStyle = {
  marginBottom: 9,
  color: "#6d28d9",
  fontSize: 12,
  fontWeight: 900,
};

const bulkBreakdownWrapStyle = {
  display: "grid",
  gap: 8,
};

const bulkBreakdownDayStyle = {
  overflow: "hidden",
  border: "1px solid #e2e8f0",
  borderRadius: 9,
  background: "#ffffff",
};

const bulkBreakdownDayHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px 10px",
  background: "#f8fafc",
  fontSize: 10,
};

const bulkBreakdownRowsStyle = {
  display: "grid",
};

const bulkBreakdownRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  padding: "8px 10px",
  borderTop: "1px solid #e2e8f0",
  color: "#334155",
  fontSize: 9,
};

const bulkEmptyStyle = {
  padding: 12,
  color: "#94a3b8",
  textAlign: "center",
  fontSize: 9,
};

const printButtonStyle = {
  border: 0,
  borderRadius: 7,
  padding: "7px 10px",
  background: "#0f766e",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const downloadPdfButtonStyle = {
  border: 0,
  borderRadius: 7,
  padding: "7px 10px",
  background: "#2563eb",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const checkedInButtonStyle = {
  border: 0,
  borderRadius: 7,
  padding: "7px 10px",
  background: "#16a34a",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const cancelBookingButtonStyle = {
  border: 0,
  borderRadius: 7,
  padding: "7px 10px",
  background: "#f59e0b",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const deleteButtonStyle = {
  border: 0,
  borderRadius: 7,
  padding: "7px 10px",
  background: "#dc2626",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

export default BookingDiaryPanel;
